import React from "react"
import { View, Pressable } from "react-native"
import { ar } from "i18n/ar"
import { Button } from "../ui/button"
import { DatePicker } from "../date-picker"
import { SupplierPicker } from "../supplier-picker"
import { ModalWrapper, Text, MCIcon } from "../core"
import { localDateToUnix, unixToLocalDate } from "@src/utils"

interface TransactionFiltersProps {
  fromDate?: number
  toDate?: number
  selectedSuppliers: number[]
  status: "active" | "cancelled"
  onFromDateChange: (date?: number) => void
  onToDateChange: (date?: number) => void
  onSuppliersChange: (ids: number[]) => void
  onStatusChange: (status: "active" | "cancelled") => void
}

export function TransactionFilters(props: TransactionFiltersProps) {
  const { fromDate, toDate, selectedSuppliers, status, onFromDateChange, onToDateChange, onSuppliersChange, onStatusChange } = props
  const [isSheetOpen, setSheetOpen] = React.useState(false)

  // Local state for lazy filtering (Mobile Sheet)
  const [localFromDate, setLocalFromDate] = React.useState(fromDate)
  const [localToDate, setLocalToDate] = React.useState(toDate)
  const [localSuppliers, setLocalSuppliers] = React.useState(selectedSuppliers)

  // Mobile Header / Sheet Trigger
  // We'll render a Filter Button that toggles the Sheet
  return (
    <>
      <View className="flex-row items-center gap-2 mb-4">
        {/* Status Toggles (Quick Access) */}
        <View className="flex-1 flex-row bg-gray-100 p-1 rounded-lg">
          <Pressable onPress={() => onStatusChange("active")} className={`flex-1 py-1.5 items-center rounded-md ${status === "active" ? "bg-white shadow-sm" : ""}`}>
            <Text className={`font-medium text-sm ${status === "active" ? "text-blue-600" : "text-gray-500"}`}>{ar.active}</Text>
          </Pressable>
          <Pressable onPress={() => onStatusChange("cancelled")} className={`flex-1 py-1.5 items-center rounded-md ${status === "cancelled" ? "bg-white shadow-sm" : ""}`}>
            <Text className={`font-medium text-sm ${status === "cancelled" ? "text-red-600" : "text-gray-500"}`}>{ar.cancelled}</Text>
          </Pressable>
        </View>

        <Button
          variant="outline"
          size="icon"
          onPress={() => {
            setLocalFromDate(fromDate)
            setLocalToDate(toDate)
            setLocalSuppliers(selectedSuppliers)
            setSheetOpen(true)
          }}
        >
          <MCIcon name="filter-variant" size={20} color="#374151" />
        </Button>
      </View>

      {isSheetOpen && (
        <ModalWrapper onClose={() => setSheetOpen(false)}>
          <View className="gap-4">
            <Text className="font-bold text-lg text-center mb-2">{ar.filterTransactions}</Text>

            <View>
              <Text className="mb-2 font-medium">{ar.dateRange}</Text>
              <View className="flex-row gap-2">
                <View className="flex-1">
                  <Text className="mb-1 text-xs">{ar.fromDate}</Text>
                  <DatePicker
                    mode="datetime"
                    value={localFromDate ? unixToLocalDate(localFromDate) : undefined}
                    onChange={(d) => setLocalFromDate(d ? localDateToUnix(d) : undefined)}
                    placeholder={ar.fromDate}
                  />
                </View>
                <View className="flex-1">
                  <Text className="mb-1 text-xs">{ar.toDate}</Text>
                  <DatePicker
                    mode="datetime"
                    value={localToDate ? unixToLocalDate(localToDate) : undefined}
                    onChange={(d) => setLocalToDate(d ? localDateToUnix(d) : undefined)}
                    placeholder={ar.toDate}
                  />
                </View>
              </View>
            </View>

            <View>
              <Text className="mb-2 font-medium">{ar.supplier}</Text>
              <SupplierPicker value={localSuppliers} onSelectionChange={setLocalSuppliers} />
            </View>

            <Button
              onPress={() => {
                onFromDateChange(localFromDate)
                onToDateChange(localToDate)
                onSuppliersChange(localSuppliers)
                setSheetOpen(false)
              }}
            >
              {ar.apply}
            </Button>
          </View>
        </ModalWrapper>
      )}
    </>
  )
}
